import * as React from 'react';
import { R } from '@/features/programs/resources/ProgramsResources.resx';
import { useExpandableGrid } from '@/features/programs/shared/lib/useExpandableGrid';
import { Button } from '@/components';

type ExpandableGridProps<T> = {
    items: T[];
    gridClassName: string;
    rowsToShow?: number;
    backgroundColor?: string;
    children: (args: {
        items: T[];
        isCoveredByOverlay: (index: number) => boolean;
    }) => React.ReactNode;
};

export const ExpandableGrid = <T,>({
    items,
    gridClassName,
    rowsToShow = 2,
    backgroundColor = 'var(--color-bg-subtle)',
    children,
}: ExpandableGridProps<T>) => {
    const gridState = useExpandableGrid(items.length, rowsToShow);
    const { expanded, gridRef } = gridState;

    const visibleItems = expanded
        ? items
        : items.slice(0, gridState.maxVisible);

    return (
        <div className="relative w-full">
            <div ref={gridRef} className={gridClassName}>
                {children({
                    items: visibleItems,
                    isCoveredByOverlay: gridState.isCoveredByOverlay,
                })}
            </div>

            {gridState.showOverlay && (
                <div
                    className="pointer-events-none absolute bottom-0 left-0 h-[350px] w-full bg-linear-to-t from-(--overlay-bg) via-(--overlay-bg)/98 to-transparent"
                    style={
                        {
                            '--overlay-bg': backgroundColor,
                        } as React.CSSProperties
                    }
                />
            )}

            {gridState.showMoreButton && (
                <div className="absolute bottom-15 left-1/2 z-10 -translate-x-1/2 transform">
                    <Button
                        variant="secondary"
                        outline={true}
                        onClick={gridState.toggleExpanded}
                        className="h-14 px-6"
                    >
                        {R.ShowMore}
                    </Button>
                </div>
            )}

            {gridState.showLessButton && (
                <div className="mt-4 flex justify-center">
                    <Button
                        variant="ghost"
                        onClick={gridState.toggleExpanded}
                        className="h-14 px-6"
                    >
                        {R.ShowLess}
                    </Button>
                </div>
            )}
        </div>
    );
};
